import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Colors } from 'planoplan-ui-kit/core/colors';
import { Input } from 'planoplan-ui-kit/core/input';
import { ButtonLoader } from 'planoplan-ui-kit/core/buttonLoader';
import { deviceDetected } from 'planoplan-ui-kit/core/device-detected';
import { useMediaQuery } from 'planoplan-ui-kit/core/hooks/useMediaQuery';
import { EMAIL_REGEXP } from '@constants/params';
import { useTranslations } from '@libs/translations';
import { Modal } from '@components/Modals';
import { Points, marginLevel, textSize } from '@styles';

const initField = {
  value: '',
  error: '',
  isValid: false,
  trigger: 0,
};
const overlayStyle = {
  backgroundColor: 'rgba(31, 31, 31, 0.5)',
};

export const EmailPopup = ({
  isOpen = false,
  onClose = () => {},
  onSubmit = () => {},
  onError = () => {},
  onOpenByHash = () => {},
  title = '',
  description = '',
  label = '',
  inputId = 'email-popup-email',
  inputLabel = '',
  placeholder = '',
  buttonText = '',
  isLoading = false,
  shouldClearOnClose = true,
  shouldClearOnSubmit = true,
  shouldCloseOnOverlayClick = true,
}) => {
  const translations = useTranslations();
  const [field, setField] = useState(initField);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isTabletPortrait = useMediaQuery(Points.tablet_portrait, { useSSR: true });

  const size = isTabletPortrait ? 'M' : 'S';
  const submitText = buttonText || translations.t('site.form.send');
  const emailPlaceholder = placeholder || translations.t('site.form.enterEmail');
  const isButtonLoading = isLoading || isSubmitting;

  const clearField = () => setField(initField);

  const onChange = ({ value, error, isValid }) => {
    setField((prevState) => ({ ...prevState, value, error, isValid }));
  };

  const onSubmitForm = async (event) => {
    event.preventDefault();

    if (!field.isValid || isButtonLoading) {
      setField((prevState) => ({ ...prevState, trigger: prevState.trigger + 1 }));
      return;
    }

    setIsSubmitting(true);

    try {
      await onSubmit(field.value);

      if (shouldClearOnSubmit) {
        clearField();
      }
    } catch (error) {
      onError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!isOpen && shouldClearOnClose) {
      clearField();
    }
  }, [isOpen, shouldClearOnClose]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onOpenByHash={onOpenByHash}
      label={label || emailPlaceholder || 'Email'}
      overlayStyle={overlayStyle}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}>
      <View>
        {title && <Title>{title}</Title>}
        {description && <Description>{description}</Description>}

        <Form onSubmit={onSubmitForm} $hasLabel={!!inputLabel}>
          <Input
            id={inputId}
            theme="primary"
            size={size}
            type="email"
            name="email"
            value={field.value}
            trigger={field.trigger}
            hasError={field.error}
            validation={{
              regexp: RegExp(EMAIL_REGEXP),
              errorValid: translations.t('site.form.invalidEmail'),
              errorEmpty: translations.t('site.form.notEmptyField'),
            }}
            device={{ isMobile: deviceDetected.isMobile }}
            placeholder={emailPlaceholder}
            aria-label={inputLabel || emailPlaceholder || 'Email'}
            onChange={onChange}
            required>
            {inputLabel}
          </Input>

          <ButtonLoader
            type="submit"
            loading={isButtonLoading}
            disabled={!field.isValid || isButtonLoading}
            theme="primary"
            size={size}
            device={{ isMobile: deviceDetected.isMobile }}>
            {submitText}
          </ButtonLoader>
        </Form>
      </View>
    </Modal>
  );
};

const View = styled.div`
  width: calc(100vw - 36px);
  max-width: 420px;
  color: ${Colors.main_black};

  @media ${Points.tablet_portrait} {
    width: 420px;
  }
`;

const Title = styled.h2`
  ${textSize('h3')};
  margin: 0;
  color: ${Colors.main_black};
`;

const Description = styled.p`
  ${textSize('p2')};
  ${marginLevel('top', 'azure')};
  margin-bottom: 0;
  color: ${Colors.main_black};
`;

const Form = styled.form`
  ${marginLevel('top', 'whisper')};

  button {
    margin-top: 10px;
  }

  @media ${Points.tablet_portrait} {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 150px;
    gap: 12px;
    align-items: start;

    button {
      margin-top: ${(p) => (p.$hasLabel ? '23px' : '0')};
    }
  }
`;
